import React, { useState } from "react";
import {Box,Button,Card,CardContent,Typography,Snackbar,Alert,CircularProgress,Table,TableBody,TableCell,TableContainer,TableHead,TableRow,Paper,} from "@mui/material";
import axios from "axios";
import * as XLSX from "xlsx";
import { BASE_URL } from "../../App";
import Swal from "sweetalert2";

const ServiceClaimCancel = () => {
  const [excelFile, setExcelFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.match(/\.(xlsx|xls)$/)) {
      setSnackbar({ open: true, message: "Please upload a valid Excel file.", severity: "warning" });
      return;
    }
    setExcelFile(file);
    setPreviewData([]);
  };

  const handleView = () => {
    if (!excelFile) {
      setSnackbar({ open: true, message: "Please upload an Excel file.", severity: "warning" });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      if (jsonData.length === 0) {
        setSnackbar({ open: true, message: "Excel file is empty.", severity: "warning" });
        return;
      }

      setPreviewData(jsonData);
    };
    reader.readAsArrayBuffer(excelFile);
  };
  const handleSubmit = async () => {
    if (previewData.length === 0) {
      Swal.fire("Warning", "Please view the Excel data first.", "warning");
      return;
    }
  
    setLoading(true);
    const formData = new FormData();
    formData.append("data", JSON.stringify(previewData)); 
  
    try {
      Swal.fire({
        title: "Processing data...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
  
  
      const res = await axios.post(`${BASE_URL}/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
  
      Swal.close();
      setSnackbar({
        open: true,
        message: res.data.message || "Data processed successfully!",
        severity: "success",
      });
    } catch (err) {
      Swal.close();
      setSnackbar({
        open: true,
        message: "Error processing data. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };
  

  const handleDownloadTemplate = async () => {
    try {
      Swal.fire({
        title: "Downloading template...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const res = await axios.get(`${BASE_URL}/downloadTemplate`, {
        responseType: "blob",
      });

      Swal.close();

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ServiceClaimCancelTemplate.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      Swal.close();
      setSnackbar({
        open: true,
        message: "Failed to download template.",
        severity: "error",
      });
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", mt: 5 }}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Service Claim Excel Upload
          </Typography>

          <Box sx={{ mt: 2 }}>
            <input
              type="file"
              accept=".xlsx, .xls"
              onChange={handleFileChange}
              style={{ marginBottom: "16px" }}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1 }}>
            <Button variant="contained" color="success" onClick={handleDownloadTemplate}>
              Download Template
            </Button>
            <Button variant="contained" color="primary" onClick={handleView}>
              View
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleSubmit}
              disabled={loading}
              startIcon={loading && <CircularProgress size={18} color="inherit" />}
            >
              Submit
            </Button>
          </Box>

          {previewData.length > 0 && (
            <TableContainer component={Paper} sx={{ mt: 4, maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {previewData[0].map((header, index) => (
                      <TableCell
                        key={index}
                        sx={{
                          backgroundColor: "lightblue",
                          fontWeight: "bold",
                          color: "black",
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.slice(1).map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <TableCell key={cellIndex}>{cell}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ServiceClaimCancel;
